import { FeatureKey } from 'common/constants/permission'
import { useFeatureEnabled } from 'core/feature-config/api/queries'
import { ScreenType } from 'layouts/dashboard'
import { useSpeciesName } from 'modules/pond/api/selector'
import {
  useAllowFeatures,
  useFirstAllowedDashboardPath,
  useIsPrimaryOwnerAndSecondaryOwnerAndAdmin,
} from 'modules/user/api/selector'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles'

import { Icon } from '@iconify/react'

import Logo from 'components/Logo'

import AccountPopover from './AccountPopover'
import MenuFarm from './MenuFarm'
import PondMenu from './MenuPond'
import { NAVBAR_MENU } from './menuConfig'

const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(10)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

interface DrawerProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  screenType: ScreenType
}

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
)

const Sidebar: React.FC<DrawerProps> = ({ open, setOpen, screenType }) => {
  const isDesktop = screenType === 'desktop'
  const theme = useTheme()
  const { t } = useTranslation('layout')
  const speciesName = useSpeciesName()
  const { farmId } = useParams()
  const navigate = useNavigate()
  const allowFeatures = useAllowFeatures()
  const firstAllowedPath = useFirstAllowedDashboardPath()
  const isOwner = useIsPrimaryOwnerAndSecondaryOwnerAndAdmin()
  // DOF-1278: the farm selector must not render (and must not fire its
  // ungated GET /farms) for a user with no farm read permission - e.g. an
  // employee whose only granted feature is IoT.
  const canReadFarm = allowFeatures.some((feature) => feature.feature.key === FeatureKey.Farm)
  const { data: isOrgOverviewEnabled } = useFeatureEnabled('ORG_OVERVIEW')
  const { data: isIotDeviceEnabled } = useFeatureEnabled('IOT_DEVICE')
  const featureFlags: Record<string, boolean> = {
    ORG_OVERVIEW: Boolean(isOrgOverviewEnabled),
    IOT_DEVICE: Boolean(isIotDeviceEnabled),
  }
  const [openSubMenus, setOpenSubMenus] = React.useState<{ [key: string]: boolean }>({})

  React.useEffect(() => {
    if (!open) setOpenSubMenus({})
  }, [open])

  const handleDrawerToggle = () => {
    setOpen((prev) => !prev)
  }

  const handleToggleSubMenu = (menuKey: string) => {
    setOpenSubMenus((prev) => ({ ...prev, [menuKey]: !prev[menuKey] }))
  }

  const handleSubItemClick = (path: string) => {
    navigate(path)
    handleDrawerToggle()
  }

  const sidebarContent = (
    <>
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={'space-between'}
          gap={1}
          ml={open ? 3 : 1}
          mr={1}
        >
          {open && (
            <Link to={firstAllowedPath} style={{ textDecoration: 'none' }}>
              <Logo />
            </Link>
          )}
          <DrawerHeader sx={{ justifyContent: open ? 'flex-end' : 'center', width: '100%' }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
            >
              {open ? (
                <img src="/static/icons/layout/sidebar-expand.svg" width={24} height={24} />
              ) : (
                <img src="/static/icons/layout/sidebar-collapse.svg" width={24} height={24} />
              )}
            </IconButton>
          </DrawerHeader>
        </Box>
      </Box>
      {!isDesktop && (
        <>
          <AccountPopover screenType={screenType} onClose={handleDrawerToggle} />
          <Divider sx={{ mb: 2 }} />
          <Box mx={2}>
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1}>
              {t('menu')}
            </Typography>
            <List disablePadding>
              {NAVBAR_MENU.filter((item) => {
                if (item.label === 'employee') return isOwner

                const permission = allowFeatures.find(
                  (feature) => feature.feature.key === item.permission
                )
                const isPermitted = item.permission ? permission?.crud_permission[1] : true
                const isFeatureEnabled = item.featureFlag
                  ? featureFlags[item.featureFlag] ?? true
                  : true
                return isPermitted && isFeatureEnabled
              }).map((item) => (
                <React.Fragment key={item.label}>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ px: 1.5, py: 1, borderRadius: 1 }}
                      onClick={() => {
                        if (item.children) {
                          handleToggleSubMenu(item.label)
                        } else if (item.onClick) {
                          item.onClick(navigate)
                          handleDrawerToggle()
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <img src={item.icon} width={24} height={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.label, { speciesName })}
                        primaryTypographyProps={{
                          fontWeight: 400,
                          color: theme.palette.common.black,
                        }}
                      />
                      {item.children && (
                        <Icon
                          icon={openSubMenus[item.label] ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                          width={20}
                          height={20}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                  {item.children && (
                    <Collapse in={openSubMenus[item.label]} unmountOnExit>
                      <List
                        disablePadding
                        sx={{ pl: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}
                      >
                        {item.children.map((child) => (
                          <ListItem disablePadding key={child.path}>
                            <ListItemButton
                              sx={{ px: 1.5, py: 0.75, borderRadius: 1 }}
                              onClick={() => handleSubItemClick(child.path)}
                            >
                              <ListItemText
                                primary={t(child.label)}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  color: theme.palette.common.black,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}
    </>
  )

  return isDesktop ? (
    <Drawer
      variant="permanent"
      open={open}
      PaperProps={{
        sx: {
          background: '#fff',
          boxShadow: 1,
          minHeight: '100vh',
        },
      }}
    >
      {sidebarContent}
      <Box sx={{ mt: 2 }}>
        {canReadFarm && <MenuFarm open={open} />}
        {farmId && <PondMenu open={open} />}
      </Box>
    </Drawer>
  ) : (
    <MuiDrawer
      variant="temporary"
      open={open}
      onClose={() => setOpen(false)}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: {
          width: '100vw',
          border: 'none',
          boxShadow: 1,
          minHeight: '100vh',
        },
      }}
    >
      {sidebarContent}
    </MuiDrawer>
  )
}

export default Sidebar
